import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPendingWork } from '../api/activity';
import { fetchAttention, fetchBudgetAlerts } from '../api/analytics';
import { useAuth } from '../auth/AuthContext';
import { buildInbox, type InboxCategory, type InboxItem } from '../attention/inbox';

const REFRESH_MS = 60_000;

export interface InboxCounts {
  all: number;
  needs_human: number;
  review: number;
  failing: number;
  budget: number;
}

export interface UseInboxResult {
  items: InboxItem[];
  counts: InboxCounts;
  hiddenOlderCount: number;
  isLoading: boolean;
  isError: boolean;
}

function emptyCounts(): InboxCounts {
  return { all: 0, needs_human: 0, review: 0, failing: 0, budget: 0 };
}

function countBy(items: InboxItem[]): InboxCounts {
  const counts = emptyCounts();
  counts.all = items.length;
  for (const it of items) {
    counts[it.category] += 1;
  }
  return counts;
}

/**
 * UX-03 — the Today inbox. Merges the three existing "something needs a
 * human" data sources (pending work, analytics attention, budget alerts)
 * through `buildInbox`. Used by the Today page (the full board) and the
 * Header nav badge (just `counts.all`), so both always agree.
 */
export function useInbox(includeOlder: boolean): UseInboxResult {
  const { currentAccount } = useAuth();
  const account = currentAccount?.id ?? undefined;

  const pendingQuery = useQuery({
    queryKey: ['activity-pending'],
    queryFn: fetchPendingWork,
    staleTime: REFRESH_MS,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
  });
  const attentionQuery = useQuery({
    queryKey: ['analytics', 'attention', account],
    queryFn: () => fetchAttention({ account }),
    staleTime: REFRESH_MS,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
  });
  const budgetQuery = useQuery({
    queryKey: ['analytics', 'budget-alerts', account],
    queryFn: () => fetchBudgetAlerts({ account }),
    staleTime: REFRESH_MS,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  const isLoading = pendingQuery.isLoading || attentionQuery.isLoading || budgetQuery.isLoading;
  const isError = pendingQuery.isError || attentionQuery.isError || budgetQuery.isError;

  const { items, hiddenOlderCount } = useMemo(() => {
    if (isLoading || isError) return { items: [] as InboxItem[], hiddenOlderCount: 0 };
    return buildInbox({
      pending: pendingQuery.data?.items ?? [],
      attention: attentionQuery.data?.items ?? [],
      budget: budgetQuery.data?.items ?? [],
      now: Date.now(),
      includeOlder,
    });
  }, [
    isLoading, isError, includeOlder,
    pendingQuery.data, attentionQuery.data, budgetQuery.data,
  ]);

  const counts = useMemo(() => countBy(items), [items]);

  return { items, counts, hiddenOlderCount, isLoading, isError };
}

export type { InboxCategory, InboxItem };
